import React, { useContext, useEffect, useState, useMemo } from "react";
import { ApiContext } from "../../../context/MyContext";
import { useUtils } from "../../../context/UtilsContext";
import { Toaster } from "../../../common/Toaster/Toaster";
import Loader from "../../../common/Loader/Loader";
import { Stack, Typography, Chip } from "@mui/material";
import {
  fetchUserPlans,
} from "../../../service/pricingPlanService";
import { getSubscriptionCommonUtils } from "../../../service/SubscriptionWellDhanService";
import AddPlanModal from "./AddPlanModal/AddPlanModal";
import Button from "../../../common/Button/Button";
import EditPricingPlanModal from "./EditPricingPlanModal/EditPricingPlanModal";
import AddPricingModal from "./AddPricingModal/AddPricingModal";
import EditPlanModal from "./EditPlanModal/EditPlanModal";
import ReusableTable from "../../../common/ReusableTable/ReusableTable";

/**
 * PlanDashboard Component
 * Main dashboard for managing subscription plans and their pricing options
 *
 * Features:
 * - View all plans in a left panel
 * - View pricing options for selected plan in right panel
 * - Add and edit plans and pricing
 * - Real-time updates after CRUD operations
 */
export const PlanDashboard = () => {
  const { loading, get } = useContext(ApiContext);
  const { sessionUtils } = useUtils();
  const eventTypeId =
    sessionUtils?.event_types?.[0]?.event_types?.find((e) => e.description === "modify_user_profile")?.id ?? null;

  // State for plans and pricing data
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [commonUtils, setCommonUtils] = useState(null); // Countries and currencies master data

  // Grouping state
  const [pricingGrouping, setPricingGrouping] = useState([]);

  // Modal states
  const [isAddPlanModalOpen, setAddPlanModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editingPricing, setEditingPricing] = useState(null);
  const [addingPricingPlanId, setAddingPricingPlanId] = useState(null);
  const [planNameForAddPricing, setPlanNameForAddPricing] = useState("");

  // Per-action loading states
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [editingPricingId, setEditingPricingId] = useState(null);

  // Toast notification state
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState("success");

  // Search/filter state
  const [planSearchQuery, setPlanSearchQuery] = useState("");
  const [pricingSearchQuery, setPricingSearchQuery] = useState("");

  /**
   * Fetch all plans and common utilities (countries, currencies) on component mount.
   * Plans are fetched only when eventTypeId is available (from sessionUtils for current app).
   */
  useEffect(() => {
    if (eventTypeId) fetchAllPlans();
    fetchCommonUtilities();
    
    const savedPricingGrouping = localStorage.getItem("planDashboardPricingGrouping");
    if (savedPricingGrouping) {
      try {
        setPricingGrouping(JSON.parse(savedPricingGrouping));
      } catch (e) {
        console.error("Error parsing pricing grouping:", e);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchAllPlans when eventTypeId available
  }, [get, eventTypeId]);

  /**
   * Fetches all subscription plans from the server
   */
  const fetchAllPlans = async () => {
    try {
      const response = await fetchUserPlans(get, eventTypeId);
      setPlans(response);

      // Auto-select first plan if none selected
      if (response.length > 0 && !selectedPlanId) {
        setSelectedPlanId(response[0].planId);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      showToast("Failed to fetch plans", "error");
    }
  };

  /**
   * Fetches common utilities like countries and denominations
   */
  const fetchCommonUtilities = async () => {
    try {
      const result = await getSubscriptionCommonUtils(get);
      if (result.success) {
        setCommonUtils(result.data);
      }
    } catch (error) {
      console.error("Error fetching common utilities:", error);
      showToast("Failed to load configuration data", "error");
    }
  };

  /**
   * Refreshes the plans list after add/edit/delete operations
   */
  const refreshPlans = async () => {
    try {
      const response = await fetchUserPlans(get, eventTypeId);
      setPlans(response);
    } catch (error) {
      console.error("Error refreshing plans:", error);
      showToast("Failed to refresh plans", "error");
    }
  };

  /**
   * Handler called when a new plan is successfully added
   */
  const handlePlanAdded = () => {
    setAddPlanModalOpen(false);
    refreshPlans();
    showToast("Plan added successfully", "success");
  };

  /**
   * Gets the currency symbol for display
   * Uses commonUtils for dynamic mapping from API
   * @param {string} currency - Currency name or symbol
   * @returns {string} Currency symbol
   */
  const getCurrencySymbol = (currency) => {
    if (!currency) return "";

    if (commonUtils?.denominations) {
      const denomination = commonUtils.denominations.find(
        (d) =>
          d.symbol?.toLowerCase() === currency.toLowerCase() ||
          d.name?.toLowerCase() === currency.toLowerCase()
      );
      if (denomination?.symbol) return denomination.symbol;
    }

    // Return original value if not found in API data
    return currency;
  };

  /**
   * Gets the display name for a country
   * Uses commonUtils for dynamic mapping from API
   * @param {string} country - Country name or code
   * @returns {string} Formatted country name
   */
  const getCountryDisplayName = (country) => {
    if (!country) return "-";

    if (commonUtils?.countries) {
      const foundCountry = commonUtils.countries.find(
        (c) => c.name?.toLowerCase() === country.toLowerCase()
      );
      if (foundCountry?.name) return foundCountry.name;
    }

    // Return original value if not found in API data
    return country;
  };

  /**
   * Helper function to show toast notifications
   * @param {string} message - Toast message
   * @param {string} type - Toast type (success/error)
   */
  const showToast = (message, type = "success") => {
    setToastMessage(message);
    setToastType(type);
  };

  // Memoized selected plan object
  const selectedPlan = useMemo(() => {
    return plans.find((plan) => plan.planId === selectedPlanId);
  }, [plans, selectedPlanId]);

  // Memoized pricing data for selected plan
  const pricingData = useMemo(() => {
    if (!selectedPlan) return [];
    return selectedPlan.pricing || [];
  }, [selectedPlan]);

  // Filtered plans based on search query
  const filteredPlans = useMemo(() => {
    if (!planSearchQuery.trim()) return plans;
    const query = planSearchQuery.toLowerCase();
    return plans.filter((plan) => {
      const planName = plan.planName?.toLowerCase() || "";
      const planDetails = plan.planDetails?.join(" ").toLowerCase() || "";
      return planName.includes(query) || planDetails.includes(query);
    });
  }, [plans, planSearchQuery]);

  // Filtered pricing data based on search query
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getCountryDisplayName is stable
  const filteredPricingData = useMemo(() => {
    if (!pricingSearchQuery.trim()) return pricingData;
    const query = pricingSearchQuery.toLowerCase();
    return pricingData.filter((pricing) => {
      const country = getCountryDisplayName(pricing.country)?.toLowerCase() || "";
      const currency = pricing.currency?.toLowerCase() || "";
      const price = String(pricing.price || "");
      return (
        country.includes(query) ||
        currency.includes(query) ||
        price.includes(query)
      );
    });
  }, [pricingData, pricingSearchQuery]);

  useEffect(() => {
    setPricingSearchQuery("");
  }, [selectedPlanId]);

  const pricingOverview = useMemo(() => {
    if (!pricingData.length) {
      return {
        optionsCount: 0,
        countriesCount: 0,
      };
    }

    const countries = new Set(
      pricingData
        .map((pricing) => getCountryDisplayName(pricing.country))
        .filter(Boolean)
    );
    return {
      optionsCount: pricingData.length,
      countriesCount: countries.size,
    };
  }, [pricingData]);

  const getPlanDurationLabel = (plan) => {
    const firstDuration = plan?.pricing?.[0]?.duration;
    if (!firstDuration) return "Duration not set";
    return `${firstDuration} month${firstDuration !== 1 ? "s" : ""}`;
  };

  /**
   * Column configuration for pricing table (right panel)
   */
  const pricingColumns = useMemo(
    () => [
      {
        accessorKey: "country",
        header: "Country",
        size: 150,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: "#374151" }}
          >
            {getCountryDisplayName(cell.getValue())}
          </Typography>
        ),
      },
      {
        accessorKey: "price",
        header: "Price",
        size: 140,
        Cell: ({ row }) => {
          const price = Number(row.original.price) || 0;
          const currency = row.original.currency;
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: "#374151",
                fontSize: "0.95rem",
              }}
            >
              {getCurrencySymbol(currency)}
              {price.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          );
        },
      },
      {
        accessorKey: "maxDiscount",
        header: "Max Discount",
        size: 120,
        Cell: ({ cell }) => (
          <Chip
            label={`${cell.getValue()}% Off`}
            size="small"
            sx={{
              backgroundColor: "#fef3c7",
              color: "#92400e",
              fontWeight: 600,
            }}
          />
        ),
      },
      {
        id: "maxDiscountedPrice",
        header: "Max Discounted Price",
        size: 190,
        Cell: ({ row }) => {
          const price = Number(row.original.price) || 0;
          const discount = Number(row.original.maxDiscount) || 0;
          const effectivePrice = price * (1 - discount / 100);
          const currency = row.original.currency;
          const hasDiscount = discount > 0;
          
          return (
            <div className="flex flex-col">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: "#059669",
                  fontSize: "1rem",
                }}
              >
                {getCurrencySymbol(currency)}
                {effectivePrice.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
              {!hasDiscount && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#6b7280",
                    fontSize: "0.7rem",
                    fontStyle: "italic",
                  }}
                >
                  No discount configured
                </Typography>
              )}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 200,
        Cell: ({ row }) => (
          <Stack 
            direction="row" 
            spacing={1.5} 
            justifyContent="center" 
            alignItems="center"
          >
            <Button
              text={editingPricingId === row.original.planPricingId ? "Editing..." : "Edit"}
              className="bg-orange-primary hover:bg-orange-600 text-white px-4 py-1.5 rounded-lg text-xs font-semibold shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => {
                setEditingPricingId(row.original.planPricingId);
                setEditingPricing({
                  ...row.original,
                  planId: selectedPlan.planId,
                  planPricingId: row.original.planPricingId,
                });
              }}
              disabled={editingPricingId === row.original.planPricingId}
              aria-label={`Edit pricing for ${getCountryDisplayName(row.original.country)} - ${row.original.duration} months`}
            />
          </Stack>
        ),
      },
    ],
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getCountryDisplayName, getCurrencySymbol are stable
    [selectedPlan, getCountryDisplayName, getCurrencySymbol, editingPricingId]
  );

  return (
    <div className="flex-1 flex flex-col min-h-screen bg-gradient-to-b from-slate-50 via-orange-50/30 to-white">
      {loading ? (
        <Loader />
      ) : (
        <div className="px-6 py-7 mx-auto w-full max-w-[1400px]">
          {/* Page Header */}
          <div className="mb-7 rounded-2xl border border-white/80 bg-white/90 shadow-[0_12px_40px_rgba(15,23,42,0.08)] backdrop-blur px-6 py-5">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                  Plans & Pricing Management
                </h1>
                <p className="text-slate-600 text-sm mt-1">
                  Choose a plan, then manage its market-wise pricing.
                </p>
              </div>
              <Button
                text="+ Add Plan"
                className="bg-orange-primary hover:bg-orange-600 text-white px-5 py-2.5 rounded-xl font-semibold shadow-[0_8px_20px_rgba(234,88,12,0.28)] w-full sm:w-auto"
                onClick={() => setAddPlanModalOpen(true)}
                aria-label="Add new plan"
              />
            </div>
            <div className="mt-4 rounded-xl border border-orange-100 bg-gradient-to-r from-orange-50 to-amber-50 px-4 py-3">
              <p className="text-sm text-orange-900 font-medium tracking-tight">
                1. Expand a plan
                <span className="mx-2 text-orange-400">→</span>
                2. Check one plan duration
                <span className="mx-2 text-orange-400">→</span>
                3. Add or edit pricing options
              </p>
            </div>
          </div>

          <div className="bg-white/95 rounded-2xl shadow-[0_14px_44px_rgba(15,23,42,0.10)] border border-slate-100 p-5">
            {plans.length > 0 && (
              <div className="mb-5">
                <input
                  type="text"
                  placeholder="Search plans by name or details..."
                  value={planSearchQuery}
                  onChange={(e) => setPlanSearchQuery(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-primary/40 focus:border-orange-300 transition-colors"
                  aria-label="Search plans"
                />
                {planSearchQuery && (
                  <p className="text-xs text-gray-500 mt-1">
                    Showing {filteredPlans.length} of {plans.length} plans
                  </p>
                )}
              </div>
            )}

            {plans.length === 0 ? (
              <div className="text-center py-12 px-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  No Plans Yet
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Create your first plan to start adding pricing options.
                </p>
                <Button
                  text="+ Create Your First Plan"
                  className="bg-orange-primary hover:bg-orange-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold shadow-sm"
                  onClick={() => setAddPlanModalOpen(true)}
                  aria-label="Create your first plan"
                />
              </div>
            ) : filteredPlans.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-gray-600 text-sm">No plans match your search.</p>
              </div>
            ) : (
              <div className="space-y-5">
                {filteredPlans.map((plan) => {
                  const isSelected = selectedPlanId === plan.planId;
                  const currentPricing = isSelected ? pricingData : [];
                  return (
                    <div
                      key={plan.planId}
                      className={`rounded-2xl border transition-all duration-200 ${
                        isSelected
                          ? "border-orange-200 bg-white shadow-[0_14px_34px_rgba(234,88,12,0.10)]"
                          : "border-slate-200 bg-white"
                      }`}
                    >
                      <div
                        className="p-5 cursor-pointer"
                        onClick={() => setSelectedPlanId(isSelected ? null : plan.planId)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedPlanId(isSelected ? null : plan.planId);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isSelected}
                        aria-label={`Toggle pricing for ${plan.planName}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="text-lg font-semibold text-slate-900 tracking-tight">
                                {plan.planName}
                              </h3>
                              {isSelected && (
                                <Chip
                                  label="Active"
                                  size="small"
                                  sx={{
                                    backgroundColor: "#ffedd5",
                                    color: "#9a3412",
                                    fontWeight: 700,
                                  }}
                                />
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              {plan.requiredEmployees} users
                            </p>
                            <div className="mt-2">
                              <Chip
                                label={`Plan duration: ${getPlanDurationLabel(plan)}`}
                                size="small"
                                sx={{
                                  backgroundColor: "#eef2ff",
                                  color: "#3730a3",
                                  fontWeight: 600,
                                }}
                              />
                            </div>
                            <ul className="mt-3 space-y-1">
                              {(plan.planDetails || []).slice(0, 3).map((detail, idx) => (
                                <li key={idx} className="text-sm text-slate-700">
                                  • {detail}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="flex flex-wrap gap-2 items-center">
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedPlanId(isSelected ? null : plan.planId)
                              }
                              className="h-9 w-9 rounded-full border border-orange-200 bg-white flex items-center justify-center hover:bg-orange-50 shadow-sm transition-colors"
                              aria-label={`${isSelected ? "Collapse" : "Expand"} ${plan.planName}`}
                            >
                              <svg
                                className={`h-4 w-4 text-orange-700 transition-transform ${
                                  isSelected ? "rotate-180" : "rotate-0"
                                }`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>
                            <Button
                              text={
                                editingPlanId === plan.planId
                                  ? "Editing..."
                                  : "Edit Plan"
                              }
                              className="bg-white border border-orange-300 hover:bg-orange-50 text-orange-700 px-4 py-2 rounded-lg text-sm font-semibold"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingPlanId(plan.planId);
                                setEditingPlan(plan);
                              }}
                              disabled={editingPlanId === plan.planId}
                              aria-label={`Edit plan ${plan.planName}`}
                            />
                          </div>
                        </div>
                      </div>

                      {isSelected && (
                        <div className="mx-4 mb-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                            <div>
                              <h4 className="text-base font-semibold text-slate-900 tracking-tight">
                                Pricing for {plan.planName}
                              </h4>
                              <p className="text-xs text-slate-500 mt-1">
                                {currentPricing.length} option
                                {currentPricing.length !== 1 ? "s" : ""} configured
                              </p>
                            </div>
                            <Button
                              text="+ Add Pricing Option"
                              className="bg-orange-primary hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap"
                              onClick={() => {
                                setAddingPricingPlanId(plan.planId);
                                setPlanNameForAddPricing(plan.planName);
                              }}
                              aria-label={`Add pricing for ${plan.planName}`}
                            />
                          </div>

                          {currentPricing.length > 0 && (
                            <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
                              <div className="flex flex-wrap gap-2">
                                <Chip
                                  label={`${pricingOverview.optionsCount} options`}
                                  size="small"
                                  sx={{ backgroundColor: "#e0f2fe", color: "#0c4a6e", fontWeight: 600 }}
                                />
                                <Chip
                                  label={`${pricingOverview.countriesCount} countries`}
                                  size="small"
                                  sx={{ backgroundColor: "#ecfccb", color: "#3f6212", fontWeight: 600 }}
                                />
                              </div>
                            </div>
                          )}

                          {currentPricing.length > 0 ? (
                            <>
                              <div className="mb-4">
                                <input
                                  type="text"
                                  placeholder="Search pricing by country, currency, or price..."
                                  value={pricingSearchQuery}
                                  onChange={(e) => setPricingSearchQuery(e.target.value)}
                                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-primary/40 focus:border-orange-300"
                                  aria-label="Search pricing options"
                                />
                                {pricingSearchQuery && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    Showing {filteredPricingData.length} of {currentPricing.length} pricing options
                                  </p>
                                )}
                              </div>
                              {filteredPricingData.length === 0 ? (
                                <div className="text-center py-10 bg-white rounded-xl border border-slate-200">
                                  <p className="text-sm text-slate-600 mb-3">
                                    No pricing options match "{pricingSearchQuery}".
                                  </p>
                                  <button
                                    onClick={() => setPricingSearchQuery("")}
                                    className="text-orange-primary hover:text-orange-600 font-semibold text-sm transition-colors"
                                    aria-label="Clear pricing search"
                                  >
                                    Clear search
                                  </button>
                                </div>
                              ) : (
                                <ReusableTable
                                  columns={pricingColumns}
                                  data={filteredPricingData}
                                  isLoading={loading}
                                  grouping={pricingGrouping}
                                  setGrouping={(newGrouping) => {
                                    const safeGrouping = Array.isArray(newGrouping)
                                      ? newGrouping.filter((g) => typeof g === "string")
                                      : [];
                                    setPricingGrouping(safeGrouping);
                                    localStorage.setItem(
                                      "planDashboardPricingGrouping",
                                      JSON.stringify(safeGrouping)
                                    );
                                  }}
                                />
                              )}
                            </>
                          ) : (
                            <div className="text-center py-10 px-4 bg-white rounded-xl border border-slate-200">
                              <h5 className="text-base font-semibold text-slate-800 mb-1">
                                No Pricing Options Yet
                              </h5>
                              <p className="text-sm text-slate-600 mb-4">
                                Add pricing options for this plan to start selling it in different markets.
                              </p>
                              <Button
                                text="+ Add First Pricing Option"
                                className="bg-orange-primary hover:bg-orange-600 text-white px-6 py-2.5 rounded-lg font-semibold shadow-sm"
                                onClick={() => {
                                  setAddingPricingPlanId(plan.planId);
                                  setPlanNameForAddPricing(plan.planName);
                                }}
                                aria-label={`Add first pricing option for ${plan.planName}`}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Modals */}
      {isAddPlanModalOpen && (
        <AddPlanModal
          onClose={() => setAddPlanModalOpen(false)}
          onSuccess={handlePlanAdded}
        />
      )}
      {editingPlan && (
        <EditPlanModal
          plan={editingPlan}
          onClose={() => {
            setEditingPlan(null);
            setEditingPlanId(null);
          }}
          onSuccess={() => {
            setEditingPlan(null);
            setEditingPlanId(null);
            refreshPlans();
            showToast("Plan updated successfully", "success");
          }}
        />
      )}
      {editingPricing && (
        <EditPricingPlanModal
          pricing={editingPricing}
          onClose={() => {
            setEditingPricing(null);
            setEditingPricingId(null);
          }}
          onSuccess={() => {
            setEditingPricing(null);
            setEditingPricingId(null);
            refreshPlans();
            showToast("Pricing updated successfully", "success");
          }}
        />
      )}
      {addingPricingPlanId && (
        <AddPricingModal
          planId={addingPricingPlanId}
          planName={planNameForAddPricing}
          onClose={() => setAddingPricingPlanId(null)}
          onSuccess={() => {
            setAddingPricingPlanId(null);
            refreshPlans();
            showToast("New pricing added successfully", "success");
          }}
        />
      )}
      {/* Toast Notification */}

      {toastMessage && <Toaster message={toastMessage} type={toastType} />}
    </div>
  );
};
